import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getAllDataPaginated } from '../../lib/supabaseUtils';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { PokemonCard } from '../../types/pokemon';
import { CardGrid } from '../../components/trades/CardGrid';
import { WantedCardSummary } from '../../components/trades/WantedCardSummary';
import { TradingFilters } from '../../components/trades/TradingFilters';
import { OwnershipFilter } from '../../components/trades/OwnershipFilterButtons';
import { PageLayout } from '../../components/layout/PageLayout';
import { Repeat } from 'lucide-react';
import { NotificationToggle } from '../../components/trades/NotificationToggle';
import { useTradeSettingsContext } from '../../contexts/TradeSettingsContext';
import { SelectedCardsGridRow } from '../../components/trades/SelectedCardsGridRow';

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  user_id: string;
  wanted_card_id: number;
  wanted_card: PokemonCard;
  mail_notification: boolean;
  trade_cards: {
    id: string;
    card: PokemonCard;
  }[];
}

interface ExtendedFilterState {
  set: string | null;
  rarity: string | null;
  search: string;
  ownership: OwnershipFilter;
  showTradable?: boolean;
}

// Interface pour étendre TradingSettings pour ajouter les propriétés nécessaires
interface ExtendedTradingSettings {
  id: number;
  key: string;
  value: string;
  eligible_sets?: string[];
  eligible_rarities?: string[];
}

export const EditTrade = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, getPathForLang } = useTranslation();
  const [trade, setTrade] = useState<Trade | null>(null);
  const [filters, setFilters] = useState<ExtendedFilterState>({
    set: null,
    rarity: null,
    search: '',
    ownership: 'all',
    showTradable: false
  });
  const [availableCards, setAvailableCards] = useState<PokemonCard[]>([]);
  const [cardQuantities, setCardQuantities] = useState<Record<number, number>>({});
  const [sets, setSets] = useState<string[]>([]);
  const [rarities, setRarities] = useState<string[]>([]);
  const [selectedCards, setSelectedCards] = useState<PokemonCard[]>([]);
  const [emailNotification, setEmailNotification] = useState<boolean>(false);
  const { settings } = useTradeSettingsContext();
  const [userCards, setUserCards] = useState<Array<{card_id: number, quantity: number, tradable: boolean}>>([]);

  // Charger les paramètres d'échange
  useEffect(() => {
    const loadSettings = async () => {
      if (settings && Array.isArray(settings) && settings.length > 0) {
        const tradingSettings = settings[0] as ExtendedTradingSettings;
        if (tradingSettings.eligible_sets) {
          setSets(tradingSettings.eligible_sets);
        }
        if (tradingSettings.eligible_rarities) {
          setRarities(tradingSettings.eligible_rarities);
        }
      }
    };

    loadSettings();
  }, [settings]);

  // Charger l'échange existant
  useEffect(() => {
    const loadTrade = async () => {
      if (!user || !id) return;

      try {
        const { data, error } = await supabase
          .from('trades')
          .select(`
            *,
            wanted_card:cards!trades_wanted_card_id_fkey(*),
            trade_cards!trade_cards_trade_id_fkey(
              id,
              card:cards!trade_cards_card_id_fkey(*)
            )
          `)
          .eq('id', id)
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        if (!data) {
          navigate(getPathForLang('/trades'));
          return;
        }

        setTrade(data);
        setEmailNotification(data.mail_notification);
        setSelectedCards(data.trade_cards.map((tc: { card: PokemonCard }) => tc.card));
      } catch (error) {
        console.error('Error loading trade:', error);
        navigate(getPathForLang('/trades'));
      }
    };

    loadTrade();
  }, [id, user, navigate, getPathForLang]);

  // Charger les cartes disponibles
  useEffect(() => {
    const loadAvailableCards = async () => {
      if (!user) return;
      if (!sets.length || !rarities.length) {
        setAvailableCards([]);
        return;
      }

      try {
        // Utiliser la pagination manuelle pour récupérer toutes les cartes utilisateur
        const allUserCards = [];
        let from = 0;
        const batchSize = 1000;
        
        while (true) {
          const { data: batch, error: batchError } = await supabase
            .from('user_cards')
            .select('card_id, quantity, tradable')
            .eq('user_id', user.id)
            .range(from, from + batchSize - 1);
          
          if (batchError) {
            console.error('Erreur lors du chargement des cartes utilisateur:', batchError);
            break;
          }
          
          if (!batch || batch.length === 0) break;
          
          allUserCards.push(...batch);
          
          if (batch.length < batchSize) break;
          from += batchSize;
        }
        
        const userCards = allUserCards;

        const quantities = (userCards || []).reduce((acc, uc) => {
          acc[uc.card_id] = uc.quantity;
          return acc;
        }, {} as Record<number, number>);

        // Utiliser getAllDataPaginated pour contourner la limitation de 1000 résultats
        console.log('Chargement de toutes les cartes avec pagination...');
        const allCards = await getAllDataPaginated('cards', '*');
        
        // Filtrer côté client pour obtenir les cartes correspondant aux critères
        const cards = allCards.filter(card => 
          sets.includes(card.set) && rarities.includes(card.rarity)
        );
        
        console.log(`Cartes filtrées: ${cards.length} sur ${allCards.length} cartes totales`);

        setAvailableCards(cards || []);
        setCardQuantities(quantities);
        
        // Stocker les informations sur les cartes de l'utilisateur
        setUserCards(userCards || []);
      } catch (error) {
        console.error('Error loading available cards:', error);
        setAvailableCards([]);
      }
    };

    loadAvailableCards();
  }, [user, sets, rarities]);

  // Filtrer les cartes disponibles
  const filteredAvailableCards = useMemo(() => {
    return availableCards.filter(card => {
      // Exclure la carte demandée des cartes proposées
      if (trade?.wanted_card && card.id === trade.wanted_card.id) {
        return false;
      }

      // Vérifier que la rareté correspond
      if (trade?.wanted_card && card.rarity !== trade.wanted_card.rarity) {
        return false;
      }

      const matchesSet = !filters.set || card.set === filters.set;
      const matchesRarity = !filters.rarity || card.rarity === filters.rarity;
      const matchesSearch = !filters.search || 
        (lang === 'en'
          ? (card.card_en?.toLowerCase().includes(filters.search.toLowerCase()) ||
             card.card_fr?.toLowerCase().includes(filters.search.toLowerCase()))
          : card.card_fr?.toLowerCase().includes(filters.search.toLowerCase()));
      const quantity = cardQuantities[card.id] || 0;
      const matchesOwnership = filters.ownership === 'all' ||
        (filters.ownership === 'owned' && quantity > 0) ||
        (filters.ownership === 'not_owned' && quantity === 0) ||
        (filters.ownership === 'duplicates' && quantity >= 3);

      // Vérifier si la carte est marquée comme échangeable
      const userCard = userCards.find(uc => uc.card_id === card.id);
      const matchesTradable = !filters.showTradable || userCard?.tradable === true;

      return matchesSet && matchesRarity && matchesSearch && matchesOwnership && matchesTradable;
    });
  }, [availableCards, filters, cardQuantities, trade?.wanted_card, lang, userCards]);

  const handleCardClick = (card: PokemonCard) => {
    setSelectedCards(prev => {
      if (prev.some(c => c.id === card.id)) {
        return prev.filter(c => c.id !== card.id);
      }
      return [...prev, card];
    });
  };

  // Fonction pour gérer le changement de carte recherchée (si ce cas se présente)
  const handleWantedCardChange = () => {
    // Réinitialiser les cartes sélectionnées quand on change la carte recherchée
    setSelectedCards([]);
  };

  const handleSave = async () => {
    if (!trade || !user) return;

    try {
      // Mettre à jour la notification par email
      const { error: updateError } = await supabase
        .from('trades')
        .update({ mail_notification: emailNotification })
        .eq('id', trade.id);

      if (updateError) throw updateError;

      // Mettre à jour les cartes proposées
      await supabase
        .from('trade_cards')
        .delete()
        .eq('trade_id', trade.id);

      const { error: insertError } = await supabase
        .from('trade_cards')
        .insert(
          selectedCards.map(card => ({
            trade_id: trade.id,
            card_id: card.id
          }))
        );

      if (insertError) throw insertError;
      navigate(getPathForLang('/trades/my-trades'));
    } catch (error) {
      console.error('Error updating trade:', error);
    }
  };

  if (!trade) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trades.breadcrumb.trades'), path: getPathForLang('/trades/browse'), icon: <Repeat className="w-4 h-4" /> },
          { label: t('trades.actions.edit'), path: getPathForLang(`/trades/${id}/edit`) }
        ]
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('trades.detail.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('create_trade.step2.subtitle')}
          </p>
        </div>

        <WantedCardSummary 
          card={trade.wanted_card}
          onChangeCard={handleWantedCardChange}
          showChangeButton={false}
        />

        <div className="bg-white shadow rounded-lg p-6">
          <TradingFilters
            filters={filters}
            onFilterChange={setFilters}
            sets={sets}
            rarities={[]}
            wantedCardRarity={trade.wanted_card.rarity || undefined}
            isWantedCardSection={false}
          />

          {/* Afficher le récapitulatif des cartes sélectionnées si au moins une carte est sélectionnée */}
          {selectedCards.length > 0 && (
            <SelectedCardsGridRow
              cards={selectedCards}
              onRemoveCard={handleCardClick}
              cardQuantities={cardQuantities}
              title={t('create_trade.selected_cards')}
              className="mb-4 mt-3"
            />
          )}

          <div className="mt-4">
            <CardGrid
              cards={filteredAvailableCards}
              selectedCards={selectedCards}
              cardQuantities={cardQuantities}
              onCardClick={handleCardClick}
              showSelectAll={true}
              onSelectAll={(cards) => setSelectedCards(cards)}
            />
          </div>
        </div>

        <div className="mt-6">
          <NotificationToggle 
            value={emailNotification}
            onChange={setEmailNotification}
          />

          <div className="flex justify-end space-x-4">
            <button
              onClick={() => navigate(getPathForLang('/trades/my-trades'))}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={selectedCards.length === 0}
              className={`px-6 py-2 rounded-md text-white font-medium
                ${selectedCards.length === 0
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}; 